import { useState, useCallback, useMemo, useRef } from 'react';
import { ZoomIn, ZoomOut, Printer, RotateCcw, RotateCw, FlipHorizontal2, Box } from 'lucide-react';
import type { ClientFloorPlan, ClientFloorPlanObject } from '../../api/portal';
import { CanvasItemShape } from './CanvasItemShape';
import type { CanvasItem } from './types';
import {
  ftToPx,
  PIXELS_PER_FOOT,
  getCanvasOrientation,
  rotateCanvas,
  type CanvasRotation,
} from './helpers';
import { isTableItem } from './types';
import { FloorPlanPrintModal } from './FloorPlanPrintModal';
import { FloorPlan3DModal } from './FloorPlan3DModal';
import { printFloorPlan, formatPrintDate, type FloorPlanPrintOptions } from './print';

const NOOP = () => {};

function toCanvasItem(o: ClientFloorPlanObject): CanvasItem {
  const config = (o.config ?? {}) as Record<string, unknown>;
  return {
    id: o.id,
    uid: typeof config.uid === 'string' && config.uid ? config.uid : o.id,
    objectType: o.objectType,
    label: o.label ?? '',
    x: o.x,
    y: o.y,
    widthFt: o.widthFt ?? 6,
    heightFt: o.heightFt ?? 6,
    rotation: o.rotation,
    color: o.color,
    opacity: o.opacity,
    seats: o.seats ?? 0,
    config,
    sortOrder: o.sortOrder,
    ...(o.tableNumber != null ? { tableNumber: o.tableNumber } : {}),
  };
}

function computeBounds(objects: ClientFloorPlanObject[]): {
  width: number;
  height: number;
} {
  if (objects.length === 0) return { width: 1200, height: 800 };
  let maxX = 0;
  let maxY = 0;
  for (const o of objects) {
    const w = ftToPx(o.widthFt ?? 6);
    const h = ftToPx(o.heightFt ?? 6);
    if (o.x + w > maxX) maxX = o.x + w;
    if (o.y + h > maxY) maxY = o.y + h;
  }
  return {
    width: Math.max(maxX + PIXELS_PER_FOOT * 4, 600),
    height: Math.max(maxY + PIXELS_PER_FOOT * 4, 400),
  };
}

interface ClientFloorPlanCanvasProps {
  plan: ClientFloorPlan;
  eventName?: string;
  /** Event start date — shown on the printed plan header when provided. */
  eventDate?: string | Date | null;
  /** Venue name — shown on the printed plan header when provided. */
  venueName?: string | null;
  /** Per-client "allowed print" permission — hides the print action when false. */
  canPrint?: boolean;
}

export function ClientFloorPlanCanvas({
  plan,
  eventName,
  eventDate,
  venueName,
  canPrint = false,
}: ClientFloorPlanCanvasProps) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState<CanvasRotation>(0);
  const [flipH, setFlipH] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [show3DModal, setShow3DModal] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const items = useMemo(() => plan.objects.map(toCanvasItem), [plan.objects]);

  const { width: canvasW, height: canvasH } = useMemo(
    () => computeBounds(plan.objects),
    [plan.objects],
  );

  const { viewW, viewH, groupTransform } = useMemo(
    () => getCanvasOrientation(canvasW, canvasH, rotation, flipH),
    [canvasW, canvasH, rotation, flipH],
  );

  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.15, 3)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.15, 0.2)), []);
  const resetZoom = useCallback(() => setZoom(1), []);
  const rotateLeft = useCallback(() => setRotation((r) => rotateCanvas(r, -1)), []);
  const rotateRight = useCallback(() => setRotation((r) => rotateCanvas(r, 1)), []);
  const toggleFlip = useCallback(() => setFlipH((f) => !f), []);

  const handlePrint = useCallback(
    (options: FloorPlanPrintOptions) => {
      const tableCount = plan.objects.filter((o) => o.tableNumber != null).length;
      const totalSeats = plan.objects.reduce((s, o) => s + (o.seats ?? 0), 0);
      printFloorPlan(
        svgRef.current,
        viewW,
        viewH,
        {
          eventName: eventName ?? plan.name,
          eventDate: formatPrintDate(eventDate),
          venueName: venueName ?? null,
          roomLabel: plan.roomName ?? null,
          planName: plan.name,
          tableCount,
          totalSeats,
          personsAssigned: null,
        },
        options,
      );
    },
    [plan, viewW, viewH, eventName, eventDate, venueName],
  );

  // Sort: background items first, then by sortOrder
  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aBg = a.config?.isBackground ? -1 : 0;
        const bBg = b.config?.isBackground ? -1 : 0;
        return aBg - bBg || a.sortOrder - b.sortOrder;
      }),
    [items],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <button
            onClick={zoomOut}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700"
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <button
            onClick={resetZoom}
            className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700 min-w-[52px] text-center"
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            onClick={zoomIn}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700"
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
          <button
            onClick={rotateLeft}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700 ml-1"
            title="Rotate left"
          >
            <RotateCcw size={16} />
          </button>
          <button
            onClick={rotateRight}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700"
            title="Rotate right"
          >
            <RotateCw size={16} />
          </button>
          <button
            onClick={toggleFlip}
            className={`p-1.5 rounded border ${
              flipH
                ? 'bg-primary-100 border-primary-300 text-primary-700'
                : 'bg-white hover:bg-gray-50 text-gray-700'
            }`}
            title="Flip horizontally"
          >
            <FlipHorizontal2 size={16} />
          </button>
          {(rotation !== 0 || flipH) && (
            <span className="text-xs text-gray-500 ml-1" data-testid="canvas-orientation">
              {rotation}°{flipH ? ' · flipped' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShow3DModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700"
            data-testid="client-view-3d-btn"
          >
            <Box size={15} />
            3D View
          </button>
          {canPrint && (
            <button
              onClick={() => setShowPrintModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700"
            >
              <Printer size={15} />
              Print
            </button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="overflow-auto rounded-lg border bg-gray-100" style={{ maxHeight: '70vh' }}>
        <div
          style={{
            width: viewW * zoom,
            height: viewH * zoom,
            position: 'relative',
            transformOrigin: 'top left',
          }}
        >
          <svg
            ref={svgRef}
            data-testid="client-floor-plan-canvas"
            width={viewW * zoom}
            height={viewH * zoom}
            viewBox={`0 0 ${viewW} ${viewH}`}
            style={{ display: 'block', background: '#f9fafb' }}
          >
            <g transform={groupTransform}>
              {sortedItems.map((item) => (
                <CanvasItemShape
                  key={item.id}
                  item={item}
                  scale={zoom}
                  isSelected={false}
                  seated={0}
                  onClick={NOOP}
                  onDragEnd={NOOP}
                  showChairs={false}
                  showLabel
                  showTableNumbers={isTableItem(item)}
                  showIcons
                />
              ))}
            </g>
          </svg>
        </div>
      </div>

      {canPrint && (
        <FloorPlanPrintModal
          open={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          onPrint={handlePrint}
        />
      )}

      <FloorPlan3DModal
        open={show3DModal}
        onClose={() => setShow3DModal(false)}
        items={items}
        room={plan.room}
        fallbackWidthFt={canvasW / PIXELS_PER_FOOT}
        fallbackHeightFt={canvasH / PIXELS_PER_FOOT}
        planName={plan.name}
      />
    </div>
  );
}
